import { CartLine } from '@interfaces/cart';

type ProductCommon = {
  title: string;
  vendor: string;
  type: string;
};

type VariantCommon = {
  sku: string;
  price: string | { amount: number };
  options?: string[];
  selectedOptions?: { name: string; value: string }[];
};

/**
 * Tracks eCommerce events by pushing them to the dataLayer.
 *
 * @param event - The name of the event (e.g., 'add_to_cart', 'remove_from_cart', 'view_cart').
 * @param payload - The eCommerce payload containing items and other details.
 */
export const trackCartEvent = (event: string, payload: Partial<EcommercePayload>) => {
  try {
    // Validate that payload has the expected structure
    if (!payload || !payload.items || !Array.isArray(payload.items)) {
      console.warn(`trackCartEvent: Invalid payload for event "${event}"`, payload);
      return;
    }

    // Filter out "Default Title" from item_variant in the payload
    const sanitizedItems = payload.items.map((item) => {
      const sanitizedItem = { ...item };
      if (
        sanitizedItem.item_variant &&
        sanitizedItem.item_variant
          .split(', ')
          .every((variant) => variant.toLowerCase() === 'default title')
      ) {
        delete sanitizedItem.item_variant; // Remove item_variant if it's only "Default Title"
      }
      return sanitizedItem;
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ecommerce: {
        currency: window?.DEPT?.localization?.currencyCode || 'USD',
        ...payload,
        items: sanitizedItems, // Use the sanitized items
      },
    } as DataLayerEvent);

    // console.log(`trackCartEvent: Successfully tracked event "${event}"`, payload);
  } catch (error) {
    console.error(`trackCartEvent: Error tracking event "${event}"`, error);
  }
};

export const trackAddToCart = (
  product: ProductCommon,
  variant: VariantCommon,
  quantity: number = 1
) => {
  const price =
    typeof variant.price === 'string'
      ? parseFloat(variant.price.replace(/[^0-9.-]+/g, ''))
      : variant.price.amount;

  const itemVariant = (variant.options || variant.selectedOptions?.map((opt) => opt.value) || [])
    .filter((value) => value.toLowerCase() !== 'default title') // Exclude "Default Title"
    .join(', ');

  const trackingItem = {
    item_id: variant.sku,
    item_name: product.title,
    price,
    quantity,
    ...(product.vendor && { item_brand: product.vendor }),
    ...(product.type && { item_category: product.type }),
    ...(itemVariant && { item_variant: itemVariant }),
  };

  trackCartEvent('add_to_cart', { items: [trackingItem] });
};

export const generateTrackingItem = (line: CartLine, quantityOverride?: number) => {
  const itemVariant = line.merchandise.selectedOptions
    .map((option) => option.value)
    .filter((value) => value.toLowerCase() !== 'default title') // Exclude "Default Title"
    .join(', ');

  return {
    item_id: line.merchandise.sku,
    item_name: line.merchandise.product.title,
    price: parseFloat(line.merchandise.price.amount),
    quantity: quantityOverride ?? line.quantity, // Use override if provided, else use line quantity
    item_brand: line.merchandise.product.vendor,
    ...(line.merchandise.product.type && { item_category: line.merchandise.product.type }),
    ...(itemVariant && { item_variant: itemVariant }), // Only include if not empty
  };
};
